import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useLiveList, useLiveSessionState, useSocket } from '../../SocketContext';
import { useLinkedPlayers } from '../../api/useLinkedPlayers';
import { liveFrameToJointPositions } from '../../skeletonConstants';
import { Banner, Breadcrumb, Button, EmptyState, ListeningState, Panel, Skel } from '../ui';
import { useElementWidth } from '../ui/useMedia';
import SkeletonView, { rootOf } from '../skeleton/SkeletonView';
import RosterStrip from './RosterStrip';
import EventFeed from './EventFeed';
import GameControlCard from './GameControlCard';
import GameSettingsCard from './GameSettingsCard';
import { Location2DCard, Location3DCard, RangesCard, SignalsCard, SukiInputCard, WhoChip } from './SensorCards';
import { clockOf, clockSecOf, durationOf, gameLabel, sessionStateOf } from './liveFormat';
import './live.css';

/** Ticks once a second so elapsed / last-frame age stay current without a data update. */
function useNow(ms = 1000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), ms);
        return () => clearInterval(id);
    }, [ms]);
    return now;
}

/**
 * Live session dashboard for one sessionId (design: LiveSession.dc.html).
 * Multiplayer pieces (roster beyond P1, devices, floor calibration) wait for the relay events;
 * the roster/focus UI is in place and shows the session's player as P1.
 */
export default function LiveSessionView({ sessionId }) {
    const { user } = useAuth();
    const isProvider = user?.role === 'provider';
    const list = useLiveList();
    const { connected, sendControl, sendParam, reconnect } = useSocket();
    const live = useLiveSessionState(sessionId);
    const linked = useLinkedPlayers();
    const now = useNow();
    const [gridRef, gridW] = useElementWidth(1200);
    const [focus, setFocus] = useState('all');
    const originRef = useRef(null);
    const lastDataAt = useRef(0);

    const listed = list.sessions.find((s) => s.sessionId === sessionId) || null;
    const game = listed?.game || live?.game || null; // id as the game sent it — used for the settings API
    const title = gameLabel(game);
    const ownerId = listed?.userId || null;
    const playerName = ownerId ? (ownerId === user?.user_id ? user?.name : linked.nameOf(ownerId)) : null;

    const joints = useMemo(() => (live?.skeleton ? liveFrameToJointPositions(live.skeleton) : null), [live?.skeleton]);
    if (joints && !originRef.current) {
        const r = rootOf(joints);
        if (r) originRef.current = [r[0], r[2]];
    }
    const origin = originRef.current;
    const status = live?.status || null;
    const st = sessionStateOf(status);
    const lastFrame = Math.max(live?.skeletonAt || 0, live?.sukiAt || 0);
    if (lastFrame) lastDataAt.current = lastFrame;

    // Phases of the page
    const refused = live?.joinError;
    const ended = live?.ended || status === 'ended';
    const gone = list.loaded && !listed && !ended;
    const waitingList = !list.loaded;
    const hasData = !!(live && (live.skeleton || live.suki || live.events.length || status));

    const crumbs = [{ label: 'Live', to: '/live' }, { label: `${title || 'Session'}${listed?.startTime ? ' · ' + clockOf(listed.startTime) : ''}` }];

    if (refused) {
        return (
            <div className="eg-page eg-page--tight">
                <Breadcrumb items={crumbs} />
                <h1 className="eg-h1">{title || 'Live session'}</h1>
                <Banner tone="danger" title="The relay refused this session.">{refused} Ask the player to share their data with you, or check the link.</Banner>
            </div>
        );
    }

    if (ended || (gone && !hasData)) {
        return (
            <div className="eg-page eg-page--tight">
                <Breadcrumb items={crumbs} />
                <h1 className="eg-h1">{title || 'Live session'}</h1>
                <EmptyState
                    tall
                    title={ended ? 'This session has ended' : 'This session isn’t live'}
                    action={<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}><Button variant="primary" to={`/sessions/${sessionId}`}>Open the recorded session</Button><Button variant="secondary" to="/live">All live sessions</Button></div>}
                />
            </div>
        );
    }

    const slots = [{ slot: 1, name: playerName || 'Player', state: status === 'disconnected' || !connected ? 'lost' : 'joined', tracker: null, movement: null }];
    const focusSlot = focus === 'all' ? 1 : focus;
    const who = <WhoChip slot={focusSlot} name={slots.find((s) => s.slot === focusSlot)?.name} />;
    const multi = slots.filter((s) => s.state === 'joined' || s.state === 'lost').length > 1;
    const bodies = joints ? [{ id: 0, joints, slot: multi ? 1 : undefined }] : [];

    const avail = gridW;
    const cols = Math.max(1, Math.min(3, Math.floor((avail + 16) / 436)));
    const span2 = Math.min(2, cols);
    const startMs = listed?.startTime ? new Date(listed.startTime).getTime() : null;
    const ageMs = lastFrame ? now - lastFrame : null;
    const recLabel = !connected ? 'Signal lost' : status === 'disconnected' ? 'Game disconnected' : status === 'paused' ? 'Paused' : 'Recording';

    const sock = !connected
        ? { text: 'Disconnected · retrying', dot: 'var(--egdl-danger)', ink: 'var(--egdl-danger)', cls: 'eg-framebadge--danger', pulse: true }
        : status === 'disconnected'
            ? { text: 'Socket connected · game offline', dot: 'var(--egdl-warning)', ink: 'var(--egdl-warning-ink)', cls: 'eg-framebadge--warning', pulse: false }
            : { text: 'Socket connected', dot: 'var(--egdl-success)', ink: 'var(--egdl-success-ink)', cls: '', pulse: false };

    return (
        <div ref={gridRef} className="eg-page eg-page--tight" data-screen-label="Live session">
            <div className="eg-page-head" style={{ gap: 12 }}>
                <Breadcrumb items={crumbs} />
                <div className="eg-title-row" style={{ gap: '16px 24px' }}>
                    <h1 className="eg-h1">{title || 'Live session'}</h1>
                    <span role="status" className={'eg-framebadge ' + sock.cls}>
                        <span className={'eg-dot' + (sock.pulse ? ' eg-dot--pulse' : '')} style={{ background: sock.dot }} />
                        <span className="eg-caps-14 eg-nowrap" style={{ color: sock.ink }}>{sock.text}</span>
                    </span>
                </div>
            </div>

            {!connected ? (
                <Banner tone="danger" title="Connection to the relay lost." action={<Button variant="primary" size="sm" onClick={reconnect}>Reconnect now</Button>}>
                    {lastDataAt.current ? `Last data ${clockSecOf(lastDataAt.current)}` : ''}
                </Banner>
            ) : null}
            {connected && status === 'disconnected' ? (
                <Banner tone="warning" title="The game lost its connection to the relay.">
                    {lastDataAt.current ? `Last data ${clockSecOf(lastDataAt.current)}` : ''}
                </Banner>
            ) : null}

            {waitingList && !hasData ? (
                <div aria-busy="true" style={{ display: 'grid', gap: 16 }}>
                    <Skel h={112} raised />
                    <Skel h={480} raised />
                </div>
            ) : !hasData ? (
                <ListeningState title="Waiting for data from the game…" />
            ) : null}

            {hasData ? (
                <>
                    <div className="ls-strip">
                        <div className="ls-rec" style={{ color: connected && status !== 'disconnected' ? 'var(--egdl-danger)' : 'var(--egdl-warning-ink)' }}>
                            <span className={'eg-dot eg-dot--lg' + (connected && status !== 'paused' ? ' eg-dot--pulse' : '')} style={{ background: connected ? st.dot : 'var(--egdl-danger)' }} />
                            {recLabel}
                        </div>
                        <div className="ls-strip__item"><span className="eg-caps-14 eg-secondary">Elapsed</span><span className="ls-strip__value">{startMs ? durationOf(now - startMs) : '—'}</span></div>
                        <div className="ls-strip__item"><span className="eg-caps-14 eg-secondary">Last data</span><span className="ls-strip__value">{ageMs == null ? '—' : ageMs < 1000 ? 'now' : `${Math.round(ageMs / 1000)} s ago`}</span></div>
                        <div className="ls-strip__item"><span className="eg-caps-14 eg-secondary">Events</span><span className="ls-strip__value">{live.events.length}</span></div>
                    </div>

                    <RosterStrip slots={slots} focus={focus} onFocus={setFocus} />

                    <div className="ls-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                        <Panel title="3D skeleton" note={joints ? (multi ? `${bodies.length} figures` : '1 figure') : undefined} style={{ gridColumn: `span ${span2}` }}>
                            <SkeletonView bodies={bodies} origin={origin} height={gridW < 600 ? 340 : 440} emptyText="Waiting for skeleton data…" />
                        </Panel>
                        <EventFeed events={live.events} height={420} fill={cols > 1} style={cols > 1 ? { gridRow: `span ${cols === 2 ? 3 : 2}`, alignSelf: 'stretch' } : undefined} />
                        <SignalsCard suki={live.suki} history={live.signalHistory} who={who} />
                        <RangesCard suki={live.suki} history={live.rangeHistory} who={who} />
                        {isProvider ? <GameControlCard status={status} connected={connected} send={(cmd) => sendControl(sessionId, cmd)} /> : null}
                        <GameSettingsCard game={game} userId={ownerId} isProvider={isProvider} send={(name, value, phase) => sendParam(sessionId, name, value, phase)} />
                        <SukiInputCard suki={live.suki} who={who} />
                        <Location2DCard trail={live.trail} origin={origin} slot={focusSlot} />
                        <Location3DCard trail={live.trail} origin={origin} />
                    </div>
                </>
            ) : null}
        </div>
    );
}
